import React from 'react';
import { RoomListEntry } from '../types';
import { Language, t } from '../data/translations';

interface RoomListProps {
    rooms: RoomListEntry[];
    onJoin: (room: RoomListEntry) => void;
    currentRoomCode?: string;
    language: Language;
    isLoading?: boolean;
    emptyMessage?: string;
}

const RoomList: React.FC<RoomListProps> = ({
    rooms,
    onJoin,
    currentRoomCode,
    language,
    isLoading,
    emptyMessage
}) => {
    // Filter out the current room if specified
    const displayRooms = (rooms || []) // Guard against undefined
        .filter(r => currentRoomCode ? r.code !== currentRoomCode : true)
        .filter(r => r.playerCount > 0);

    if (isLoading) {
        return <div className="text-center text-[#5D4037] py-8 font-['Cinzel'] font-bold animate-pulse">{t('multiplayer.connecting', language)}</div>;
    }

    if (displayRooms.length === 0) {
        return (
            <div className="text-center text-[#5D4037]/60 py-12 italic border-2 border-dashed border-[#8B4513]/20 rounded-xl">
                {emptyMessage || t('multiplayer.noRooms', language)}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {displayRooms.map((room) => (
                <button
                    key={room.code}
                    type="button"
                    onClick={() => onJoin(room)}
                    className="w-full flex items-center justify-between p-4 bg-[#F5DEB3]/30 hover:bg-[#EEDC82]/50 rounded-lg border border-[#8B4513]/20 hover:border-[#DAA520]/50 transition-all text-left shadow-sm group relative overflow-hidden"
                >
                    {/* Decorative side bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#8B4513]/40 group-hover:bg-[#DAA520] transition-colors"></div>
                    
                    <div className="flex flex-col overflow-hidden pl-2">
                        <div className="flex items-center gap-2">
                            {room.isLocked && (
                                <span className="text-amber-800 text-sm flex-shrink-0" title={t('multiplayer.passwordRequired', language)}>🔒</span>
                            )}
                            <span className="font-['Cinzel'] font-black text-[#3E2723] truncate max-w-[180px] group-hover:text-[#8B4513] transition-colors text-lg">
                                {room.roomName}
                            </span>
                        </div>
                        <span className="text-xs text-[#5D4037] font-serif italic truncate">
                            {t('multiplayer.host', language)}: <span className="font-bold">{room.hostName}</span>
                        </span>
                    </div>
                    
                    <div className="flex flex-col items-end gap-1">
                        <div className={`text-xs font-black px-2 py-1 rounded-full border ${room.playerCount < 2 ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                            {room.playerCount}/2
                        </div>
                        <span className="text-[10px] text-[#8B4513]/60 uppercase tracking-widest font-bold group-hover:text-[#8B4513] transition-colors">
                            {t('ui.join', language)} →
                        </span>
                    </div>
                </button>
            ))}
        </div>
    );
};

export default RoomList;
